import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import {
  eurosLabel,
  seriesFinishedLabel,
  seriesPlayerLines,
  seriesSideLine,
  seriesStateLabel,
  seriesStateTone,
  seriesTargetLines,
} from '@/lib/npc-replays-labels';
import { REPLAY_MODE_LABEL, replayStartedLabel } from '@/lib/replay-mark';
import type { NpcSeriesList, NpcSeriesRow } from '@matter_extractor/shared';
import { Spinner } from '@pms-digital/design-system';

/**
 * L'onglet « Séries » (npc.md §9; maquette `Bibliotheque`, planche « d. L'onglet Séries »): every
 * launch, most recent first - the date and who launched it, the bench or the NPC, the experience
 * and its version with the side, the mode, the player, the state, the replays finished, and the
 * cost of the device with the player's under it (H15).
 *
 * No column compares two series (N10): the table lists what the launches froze and what the
 * sessions recorded, and the comparison stays in Claude. A series seeded after the fact carries its
 * label - « jouée avant les NPC » - as a quiet pill under its name.
 *
 * ## A row is a link to the page of its series
 *
 * §9 sends a row to `/admin/npc/series/<id>`. The page that lists the series does not know that
 * route's type, so `renderName` is the slot it plugs the link into, given the row and what to draw;
 * left out, the name is plain text (the tests of the table alone).
 *
 * `series` is `undefined` while the read is in flight, `null` when it failed - which on a server
 * that has not got the route yet reads as an error, never as « aucune série ».
 */

export interface NpcSeriesTableProps {
  series: NpcSeriesList | null | undefined;
  /** The link to the series' page, around the name of the bench or of the NPC. */
  renderName?: (row: NpcSeriesRow, name: string) => React.ReactNode;
}

const HEAD =
  'px-2 py-2 text-left font-semibold text-gray-600 text-caption uppercase tracking-wide whitespace-nowrap';
const CELL = 'px-2 py-3 align-top text-small';

export function NpcSeriesTable({ series, renderName }: NpcSeriesTableProps) {
  if (series === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Chargement des séries" />
      </div>
    );
  }
  if (series === null) {
    return (
      <p data-testid="npc-series-erreur" className="text-gray-700">
        Les séries n’ont pas pu être chargées.
      </p>
    );
  }
  if (series.series.length === 0) {
    return (
      <div
        data-testid="npc-series-vide"
        className="rounded-md border-2 border-gray-300 border-dashed px-6 py-10 text-center"
      >
        <h2 className="font-semibold text-body-lg text-gray-900">Aucune série pour l’instant</h2>
        <p className="mx-auto max-w-prose pt-1 text-gray-600 text-small">
          Une série naît quand on lance un banc ou un NPC sur une expérience.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table data-testid="npc-series" className="w-full min-w-[56rem] border-collapse">
        <thead>
          <tr className="border-gray-200 border-b">
            {['Date', 'Banc ou NPC', 'Expérience', 'Mode', 'Joueur', 'État'].map((label) => (
              <th key={label} scope="col" className={HEAD}>
                {label}
              </th>
            ))}
            {['Rejeux finis', 'Coût'].map((label) => (
              <th key={label} scope="col" className={`${HEAD} text-right`}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.series.map((row) => (
            <SeriesRow
              key={row.id}
              row={row}
              {...(renderName === undefined ? {} : { renderName })}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeriesRow({
  row,
  renderName,
}: { row: NpcSeriesRow } & Pick<NpcSeriesTableProps, 'renderName'>) {
  const target = seriesTargetLines(row);
  const player = seriesPlayerLines(row.player);
  return (
    <tr data-testid="npc-serie" data-state={row.state} className="border-gray-100 border-b">
      <td className={`${CELL} whitespace-nowrap`}>
        {replayStartedLabel(row.createdAt)}
        <span className="block text-caption text-gray-600">{row.createdBy}</span>
      </td>
      <td className={CELL}>
        {renderName === undefined ? (
          <span className="font-semibold text-bleu-nuit">{target.name}</span>
        ) : (
          renderName(row, target.name)
        )}
        <span className="block text-caption text-gray-600">{target.detail}</span>
        {row.label === null ? null : (
          <span
            data-testid="npc-serie-etiquette"
            className="mt-1 inline-block rounded-sm border border-gray-300 bg-white px-1.5 py-0.5 text-caption text-gray-700 leading-4"
          >
            {row.label}
          </span>
        )}
      </td>
      <td className={CELL}>
        <span className="font-semibold text-bleu-nuit">{row.experienceName}</span>
        {row.experienceVersionNumber === null ? null : (
          <span className="text-caption text-gray-600"> v{row.experienceVersionNumber}</span>
        )}
        <span className="block text-caption text-gray-600">{seriesSideLine(row.tag)}</span>
      </td>
      <td className={CELL}>{REPLAY_MODE_LABEL[row.mode]}</td>
      <td className={CELL}>
        {player.main}
        <span className="block text-caption text-gray-600">{player.detail}</span>
      </td>
      <td className={CELL}>
        <StatusBadge tone={seriesStateTone(row.state)} data-testid="npc-serie-etat">
          {seriesStateLabel(row)}
        </StatusBadge>
        {row.runningCount > 0 && row.state === 'running' ? (
          <span className="mt-1 block text-caption text-gray-600">
            {row.runningCount} en jeu,{' '}
            {Math.max(0, row.slotCount - row.finishedCount - row.runningCount)}{' '}
            {row.slotCount - row.finishedCount - row.runningCount > 1 ? 'attendent' : 'attend'}
          </span>
        ) : null}
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>{seriesFinishedLabel(row)}</td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {eurosLabel(row.costEur)}
        <span className="block text-caption text-gray-600">
          {row.player.kind === 'claude'
            ? 'joueur gratuit'
            : row.playerCostEur > 0
              ? `+ joueur ${eurosLabel(row.playerCostEur)}`
              : ''}
        </span>
      </td>
    </tr>
  );
}
